import * as React from 'react';
import { breakpoints, BreakpointTypes } from '../types/breakpoints';
import { matchMediaSafe } from './matchMediaSafe';

export type ViewportSize = BreakpointTypes;

type ViewportSizeRange = {
	// Ensures that all breakpoints are accounted for
	readonly [Breakpoint in BreakpointTypes]: {
		min: string;
		max: string;
	};
};

const viewportSizeRange: ViewportSizeRange = {
	xs: {
		min: '0',
		max: `${breakpoints.sm - 1}px`,
	},
	sm: {
		min: `${breakpoints.sm}px`,
		max: `${breakpoints.md - 1}px`,
	},
	md: {
		min: `${breakpoints.md}px`,
		max: `${breakpoints.lg - 1}px`,
	},
	lg: {
		min: `${breakpoints.lg}px`,
		max: `${breakpoints.xl - 1}px`,
	},
	xl: {
		min: `${breakpoints.xl}px`,
		max: `100vw`,
	},
};

const queryCache = new Map<BreakpointTypes, MediaQueryList>();

const getQuery = (breakpoint: BreakpointTypes): MediaQueryList => {
	let query = queryCache.get(breakpoint);

	if (query === undefined) {
		const { min, max } = viewportSizeRange[breakpoint];
		query = matchMediaSafe(`(min-width: ${min}) and (max-width: ${max})`);
		queryCache.set(breakpoint, query);
	}

	return query;
};

/**
 * Determines whether the current viewport matches the specified one
 * @param viewportSize The viewport size to match against
 * @returns Whether it matches
 */
export const useIsViewportSize = (viewportSize: ViewportSize): boolean => {
	const [result, setResult] = React.useState(() => getQuery(viewportSize).matches);

	React.useEffect(() => {
		const query = getQuery(viewportSize);

		const onChange = (event: MediaQueryListEvent): void => {
			setResult(event.matches);
		};

		query.addEventListener('change', onChange);

		return () => {
			query.removeEventListener('change', onChange);
		};
	}, [viewportSize]);

	return result;
};
